<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useSessionStore } from '@/stores/session';
import RefereePreviewModal from '@/components/RefereePreviewModal.vue';
import ChangePasswordModal from '@/components/ChangePasswordModal.vue';
import { UserSearch, LogOut, ChevronDown, KeyRound } from 'lucide-vue-next';

const session = useSessionStore();
const route = useRoute();
const router = useRouter();

// The single "Admin" menu — secondary admin screens only. The day-to-day
// panels live on the dashboard (the three coloured boxes), reached via the
// brand logo / "Back to panels".
const ADMIN_ITEMS = [
    { to: '/admin/users',    label: 'Users' },
    { to: '/admin/settings', label: 'Settings' },
];

const menuOpen = ref(false);     // mobile drawer
const adminOpen = ref(false);    // desktop Admin dropdown
const previewOpen = ref(false);
const passwordOpen = ref(false);

// The dropdown is teleported to <body> (the header has overflow:hidden),
// so we anchor it to the button with fixed coordinates.
const adminBtn = ref<HTMLElement | null>(null);
const popStyle = ref<Record<string, string>>({});

function toggleMenu() { menuOpen.value = !menuOpen.value; }
function closeMenu()  { menuOpen.value = false; }
async function toggleAdmin() {
    adminOpen.value = !adminOpen.value;
    if (!adminOpen.value) return;
    await nextTick();
    const r = adminBtn.value?.getBoundingClientRect();
    if (r) popStyle.value = { top: `${Math.round(r.bottom + 6)}px`, right: `${Math.round(window.innerWidth - r.right)}px` };
}
function closeAdmin()  { adminOpen.value = false; }
function openPreview() { previewOpen.value = true; closeMenu(); closeAdmin(); }
function closePreview(){ previewOpen.value = false; }
function openPassword() { passwordOpen.value = true; closeMenu(); closeAdmin(); }
function closePassword(){ passwordOpen.value = false; }

// Auto-close menus on route change.
watch(() => route.fullPath, () => { closeMenu(); closeAdmin(); });

async function onLogout() {
    closeMenu();
    await session.logout();
    router.push({ name: 'login' });
}

function isActive(to: string): boolean {
    if (to === '/') return route.path === '/' || route.path === '';
    return route.path.startsWith(to);
}
</script>

<template>
    <header class="aravo-topbar no-print">
        <!-- Decorative orbs for a 3D feel against the deep teal. -->
        <span class="aravo-topbar-orb aravo-topbar-orb-a" aria-hidden="true"></span>
        <span class="aravo-topbar-orb aravo-topbar-orb-b" aria-hidden="true"></span>

        <div class="aravo-topbar-inner">
            <!-- Brand: 3D-feeling rotated logo tile + minimalist wordmark -->
            <RouterLink to="/" class="aravo-brand">
                <span class="aravo-brand-tile">
                    <img src="/aravo-logo.jpg" alt="ARAVO" />
                </span>
                <span class="aravo-brand-text">
                    <span class="aravo-brand-name">
                        <span class="hidden sm:inline">ARAVO · Referee <span class="aravo-brand-accent">Designations</span></span>
                        <span class="sm:hidden">ARAVO</span>
                    </span>
                    <span class="aravo-brand-sub hidden sm:block">
                        Association Rwandaise des Arbitres de Volleyball
                    </span>
                </span>
            </RouterLink>

            <!-- Desktop nav -->
            <nav class="aravo-nav">
                <RouterLink v-if="session.isReferee" to="/referee"
                    class="aravo-nav-pill"
                    :class="isActive('/referee') ? 'is-active' : ''">
                    My matches
                </RouterLink>

                <!-- The single Admin menu (Users + Settings). -->
                <div v-if="session.isAdmin" class="aravo-admin-menu">
                    <button ref="adminBtn" @click="toggleAdmin" type="button"
                            class="aravo-nav-pill aravo-admin-btn"
                            :class="adminOpen ? 'is-active' : ''"
                            :aria-expanded="adminOpen">
                        Admin <ChevronDown :size="14" :stroke-width="2.6" />
                    </button>
                    <Teleport to="body">
                        <div v-if="adminOpen" class="aravo-admin-backdrop" @click="closeAdmin"></div>
                        <div v-if="adminOpen" class="aravo-admin-pop" :style="popStyle">
                            <RouterLink v-for="i in ADMIN_ITEMS" :key="i.to" :to="i.to"
                                        class="aravo-admin-item" @click="closeAdmin">
                                {{ i.label }}
                            </RouterLink>
                        </div>
                    </Teleport>
                </div>

                <!-- Admin-only: Preview as referee. Sits next to Sign off
                     so the admin can step into a referee's shoes in one click. -->
                <button v-if="session.isAdmin" @click="openPreview"
                        class="aravo-action-btn aravo-action-preview"
                        aria-label="Preview as referee">
                    <UserSearch :size="15" />
                    <span>Preview as referee</span>
                </button>

                <span v-if="session.user && !session.isAdmin" class="aravo-user-chip">
                    {{ session.user.username }} · {{ session.role }}
                </span>

                <!-- Any signed-in user can change their own password (admin or referee).
                     Sits just before Sign off so it reads as an account-action. -->
                <button v-if="session.isAuthed" @click="openPassword"
                        class="aravo-action-btn aravo-action-password"
                        aria-label="Change my password">
                    <KeyRound :size="15" />
                    <span>Change password</span>
                </button>

                <button v-if="session.isAuthed" @click="onLogout"
                        class="aravo-action-btn aravo-action-signoff">
                    <LogOut :size="15" />
                    <span>Sign off</span>
                </button>
            </nav>

            <!-- Mobile hamburger -->
            <button v-if="session.isAuthed"
                    type="button"
                    @click="toggleMenu"
                    :aria-expanded="menuOpen"
                    aria-label="Toggle menu"
                    class="aravo-burger">
                <span class="aravo-burger-glyph">
                    <span :class="menuOpen ? 'is-x-top' : ''"></span>
                    <span :class="menuOpen ? 'is-x-mid' : ''"></span>
                    <span :class="menuOpen ? 'is-x-bot' : ''"></span>
                </span>
            </button>
        </div>

        <!-- Mobile panel -->
        <div v-if="menuOpen" class="aravo-mobile">
            <RouterLink v-if="session.isReferee" to="/referee"
                @click="closeMenu"
                class="aravo-mobile-pill"
                :class="isActive('/referee') ? 'is-active' : ''">
                My matches
            </RouterLink>
            <RouterLink v-if="session.isAdmin" to="/admin"
                @click="closeMenu"
                class="aravo-mobile-pill"
                :class="route.path === '/admin' ? 'is-active' : ''">
                Panels
            </RouterLink>
            <RouterLink v-for="i in (session.isAdmin ? ADMIN_ITEMS : [])" :key="i.to" :to="i.to"
                @click="closeMenu"
                class="aravo-mobile-pill"
                :class="isActive(i.to) ? 'is-active' : ''">
                {{ i.label }}
            </RouterLink>
            <button v-if="session.isAdmin" @click="openPreview" class="aravo-mobile-pill is-action">
                <UserSearch :size="15" /><span>Preview as referee</span>
            </button>
            <button v-if="session.isAuthed" @click="openPassword" class="aravo-mobile-pill is-action is-password">
                <KeyRound :size="15" /><span>Change password</span>
            </button>
            <div v-if="session.user" class="aravo-mobile-foot">
                <span class="aravo-user-chip">
                    {{ session.user.username }} · {{ session.role }}
                </span>
                <button @click="onLogout" class="aravo-action-btn aravo-action-signoff">
                    <LogOut :size="15" /><span>Sign off</span>
                </button>
            </div>
        </div>

        <!-- The preview-as-referee modal is mounted from here so it's
             always available to the admin from anywhere in the app. -->
        <RefereePreviewModal :open="previewOpen" @close="closePreview" />

        <!-- Self-service change-password modal, available to any signed-in user. -->
        <ChangePasswordModal :open="passwordOpen" @close="closePassword" />
    </header>
</template>

<style scoped>
/* ════════════════════════════════════════════════════════════════════
   ARAVO top bar — teal-gradient ribbon with 3D-feel logo tile.
   Sits sticky at the top across every authed view.
═══════════════════════════════════════════════════════════════════════ */
.aravo-topbar {
    position: sticky; top: 0; z-index: 50;
    overflow: hidden;
    /* Deep forest-green title strip with a 3px yellow underscore so the
       brand band reads as the federation's stationery. */
    background:
        radial-gradient(circle at 15% 12%, rgba(110, 231, 183, 0.22), transparent 55%),
        radial-gradient(circle at 85% 88%, rgba(5, 150, 105, 0.20), transparent 60%),
        linear-gradient(110deg, #022C22 0%, #064E3B 50%, #065F46 100%);
    border-bottom: 3px solid #FACC15;   /* federation yellow accent */
    box-shadow: 0 10px 24px -12px rgba(0, 0, 0, 0.35);
    color: #ecfdf5;
}
.aravo-topbar-orb {
    position: absolute; border-radius: 50%; filter: blur(50px);
    pointer-events: none;
}
.aravo-topbar-orb-a { width: 200px; height: 200px; top: -90px; left: -60px; background: rgba(110, 231, 183, 0.30); }
.aravo-topbar-orb-b { width: 240px; height: 240px; top: -130px; right: -80px; background: rgba(5, 150, 105, 0.25); }

.aravo-topbar-inner {
    position: relative; z-index: 1;
    max-width: 80rem; margin: 0 auto;
    padding: 0.65rem 0.9rem;
    display: flex; align-items: center; gap: 0.85rem;
}

/* ─── Brand ─── */
.aravo-brand {
    display: inline-flex; align-items: center; gap: 0.7rem;
    text-decoration: none; color: #f0fdfa;
    min-width: 0; flex: 1 1 auto;
}
.aravo-brand-tile {
    position: relative;
    flex-shrink: 0;
    width: 2.55rem; height: 2.55rem;
    border-radius: 0.7rem;
    background: linear-gradient(160deg, #ffffff, #ecfeff);
    box-shadow:
        0 12px 22px -10px rgba(15, 118, 110, 0.55),
        0 4px 10px -6px rgba(0, 0, 0, 0.35),
        inset 0 1px 0 rgba(255, 255, 255, 0.9);
    transform: rotate(-6deg);
    display: inline-flex; align-items: center; justify-content: center;
}
.aravo-brand-tile img {
    width: 1.9rem; height: 1.9rem; object-fit: contain;
    transform: rotate(6deg);
    filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.25));
}
.aravo-brand-text { display: flex; flex-direction: column; min-width: 0; line-height: 1.15; }
.aravo-brand-name {
    font-size: 0.82rem; font-weight: 800; letter-spacing: 0.04em;
    text-transform: uppercase; color: #ffffff;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
/* Federation yellow accent on the "Designations" word so the
   product noun pops out of the brand strip. */
.aravo-brand-accent { color: #FACC15; font-weight: 800; }
.aravo-brand-sub {
    font-size: 0.66rem; color: rgba(204, 251, 241, 0.78);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    margin-top: 0.05rem;
}

/* ─── Nav (desktop ≥ md) ─── */
.aravo-nav { display: none; align-items: center; gap: 0.45rem; }
@media (min-width: 768px) { .aravo-nav { display: inline-flex; } }

.aravo-nav-pill {
    padding: 0.4rem 0.95rem;
    border-radius: 9999px;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.22);
    color: #ffffff;
    font-size: 0.74rem; font-weight: 700; letter-spacing: 0.06em;
    text-transform: uppercase; text-decoration: none;
    transition: background 120ms, border-color 120ms, transform 120ms;
}
.aravo-nav-pill:hover { background: rgba(255, 255, 255, 0.22); }
.aravo-nav-pill.is-active {
    background: linear-gradient(135deg, #FACC15 0%, #EAB308 100%);
    color: #022C22;
    border-color: rgba(202, 138, 4, 0.7);
    box-shadow: 0 6px 14px -8px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.55);
}

.aravo-user-chip {
    margin-left: 0.35rem;
    padding: 0.3rem 0.7rem;
    border-radius: 9999px;
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff;
    font-size: 0.72rem; font-weight: 600;
    white-space: nowrap;
}

/* ─── Admin dropdown (single admin menu: Users + Settings) ─── */
.aravo-admin-menu { position: relative; }
.aravo-admin-btn {
    display: inline-flex; align-items: center; gap: 0.25rem;
    cursor: pointer;
}
/* Teleported to <body>, anchored to the button via inline top/right. */
.aravo-admin-backdrop {
    position: fixed; inset: 0; z-index: 55;
    background: transparent;
}
.aravo-admin-pop {
    position: fixed; z-index: 56;
    min-width: 10rem;
    background: #ffffff;
    border: 1px solid rgba(6, 78, 59, 0.16);
    border-radius: 0.7rem;
    box-shadow: 0 20px 40px -18px rgba(2, 44, 34, 0.55);
    padding: 0.35rem;
}
.aravo-admin-item {
    display: block;
    padding: 0.5rem 0.65rem;
    border-radius: 0.5rem;
    color: #064E3B;
    text-decoration: none;
    font-size: 0.8rem; font-weight: 700;
    letter-spacing: 0.02em;
    transition: background 120ms;
}
.aravo-admin-item:hover { background: #f0fdf4; }

/* ─── Action buttons (Preview / Sign off) ─── */
.aravo-action-btn {
    display: inline-flex; align-items: center; gap: 0.35rem;
    padding: 0.4rem 0.9rem;
    border-radius: 9999px;
    font-size: 0.72rem; font-weight: 700; letter-spacing: 0.04em;
    cursor: pointer;
    transition: transform 120ms, background 120ms, box-shadow 120ms;
}
.aravo-action-btn :deep(svg) { flex-shrink: 0; }

.aravo-action-preview {
    background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
    color: #ffffff;
    border: 1px solid rgba(45, 212, 191, 0.6);
    box-shadow:
        0 8px 16px -8px rgba(20, 184, 166, 0.65),
        inset 0 1px 0 rgba(255, 255, 255, 0.3);
}
.aravo-action-preview:hover { transform: translateY(-1px); box-shadow: 0 12px 20px -8px rgba(20, 184, 166, 0.8); }

.aravo-action-signoff {
    background: rgba(255, 255, 255, 0.08);
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.4);
}
.aravo-action-signoff:hover { background: rgba(255, 255, 255, 0.18); }

/* Change-password pill — neutral white-on-translucent so it doesn't
   compete with the teal Preview button, but still reads as an action. */
.aravo-action-password {
    background: rgba(250, 204, 21, 0.18);
    color: #FEF3C7;
    border: 1px solid rgba(250, 204, 21, 0.55);
}
.aravo-action-password:hover { background: rgba(250, 204, 21, 0.30); color: #ffffff; }

/* ─── Mobile hamburger + drawer ─── */
.aravo-burger {
    display: inline-flex; align-items: center; justify-content: center;
    width: 2.5rem; height: 2.5rem;
    border-radius: 0.55rem;
    border: 1px solid rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.06);
    color: #ffffff; cursor: pointer;
    transition: background 120ms;
    flex-shrink: 0;
}
@media (min-width: 768px) { .aravo-burger { display: none; } }
.aravo-burger:hover { background: rgba(255, 255, 255, 0.16); }
.aravo-burger-glyph { position: relative; width: 1.1rem; height: 0.8rem; }
.aravo-burger-glyph span {
    position: absolute; left: 0; right: 0; height: 2px; background: #ffffff;
    border-radius: 999px;
    transition: transform 160ms ease, opacity 160ms ease;
}
.aravo-burger-glyph span:nth-child(1) { top: 0; }
.aravo-burger-glyph span:nth-child(2) { top: 0.35rem; }
.aravo-burger-glyph span:nth-child(3) { top: 0.7rem; }
.aravo-burger-glyph .is-x-top { transform: translateY(0.35rem) rotate(45deg); }
.aravo-burger-glyph .is-x-mid { opacity: 0; }
.aravo-burger-glyph .is-x-bot { transform: translateY(-0.35rem) rotate(-45deg); }

.aravo-mobile {
    position: relative; z-index: 1;
    border-top: 1px solid rgba(250, 204, 21, 0.45);
    background: linear-gradient(180deg, rgba(6, 78, 59, 0.95), rgba(2, 44, 34, 0.97));
    /* The bottom inset accounts for the iOS home indicator + dynamic
       island devices when the drawer is the lowest panel on-screen. */
    padding: 0.75rem 0.9rem max(0.9rem, env(safe-area-inset-bottom, 0));
    display: flex; flex-direction: column; gap: 0.5rem;
}
.aravo-mobile-pill {
    display: inline-flex; align-items: center; gap: 0.4rem;
    padding: 0.65rem 0.95rem; border-radius: 0.7rem;
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff; text-decoration: none;
    font-size: 0.82rem; font-weight: 700; letter-spacing: 0.04em;
    text-transform: uppercase;
    border: 1px solid rgba(255, 255, 255, 0.18);
    cursor: pointer; transition: background 120ms;
}
.aravo-mobile-pill:hover { background: rgba(255, 255, 255, 0.22); }
.aravo-mobile-pill.is-active {
    background: linear-gradient(135deg, #FACC15 0%, #EAB308 100%);
    color: #022C22;
    border-color: rgba(202, 138, 4, 0.7);
}
.aravo-mobile-pill.is-action {
    background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
    border-color: rgba(45, 212, 191, 0.55);
}
.aravo-mobile-pill.is-action.is-password {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    border-color: rgba(245, 158, 11, 0.6);
}
.aravo-mobile-foot {
    display: flex; align-items: center; justify-content: space-between;
    gap: 0.55rem; padding-top: 0.65rem;
    border-top: 1px solid rgba(255, 255, 255, 0.14);
}
</style>
